import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, CreditCard, AlertTriangle, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  trialing: { label: "Free Trial", color: "bg-amber-100 text-amber-800 border-amber-200" },
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  past_due: { label: "Past Due", color: "bg-red-100 text-red-800 border-red-200" },
  canceled: { label: "Cancelled", color: "bg-slate-100 text-slate-700 border-slate-200" },
  none: { label: "No subscription", color: "bg-slate-100 text-slate-700 border-slate-200" },
};

const FEATURES = ["Unlimited leads & customers", "Public lead capture form", "Follow-up automation engine", "Appointments & calendar", "Analytics & reporting", "Unlimited team members"];

export default function Billing() {
  const { user, refresh } = useAuth();
  const canManage = ["owner", "admin"].includes(user?.role);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/billing/me").then(({ data }) => setInfo(data));
  useEffect(() => { load(); }, []);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data: ctx } = await api.post("/billing/checkout-context");
      const clientToken = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
      if (ctx.mock || !clientToken || !window.Paddle) {
        // Paddle credentials not configured — activate via mock so the flow is demonstrable.
        await api.post("/billing/activate-mock");
        toast.success("Subscription activated (mock mode — connect Paddle keys to charge real cards)");
        await load(); await refresh();
      } else {
        window.Paddle.Checkout.open({ items: [{ priceId: ctx.price_id, quantity: 1 }], customData: { checkout_nonce: ctx.nonce, business_id: user.business_id }, settings: { displayMode: "overlay" } });
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async () => { setBusy(true); try { await api.post("/billing/cancel"); toast.success("Subscription cancelled"); await load(); await refresh(); } finally { setBusy(false); } };
  const resume = async () => { setBusy(true); try { await api.post("/billing/resume"); toast.success("Subscription resumed"); await load(); await refresh(); } finally { setBusy(false); } };

  if (!info) return <div className="space-y-6"><h1 className="text-2xl font-bold font-heading">Billing</h1><Skeleton className="h-64 rounded-xl" /></div>;

  const sub = info.subscription;
  const meta = STATUS_META[sub.status] || STATUS_META.none;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading">Billing & Subscription</h1>
        <p className="text-slate-500 mt-1">Manage your PlumbFlow Pro plan.</p>
      </div>

      {info.mock && (
        <Card className="p-4 border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="billing-mock-banner">
          <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Payment provider not connected.</strong> Paddle API keys are not configured, so subscription actions run in <strong>mock mode</strong> (no real charges). Add <code className="font-mono">PADDLE_API_KEY</code>, <code className="font-mono">PADDLE_PRICE_ID</code>, <code className="font-mono">PADDLE_WEBHOOK_SECRET</code> and the frontend client token to go live.
          </div>
        </Card>
      )}

      <Card className="p-6 border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-blue-600 uppercase tracking-wider">PlumbFlow Pro</div>
            <div className="flex items-end gap-1 mt-1"><span className="text-4xl font-extrabold font-heading">${info.price}</span><span className="text-slate-500 mb-1">/month</span></div>
          </div>
          <Badge className={`border ${meta.color} text-sm px-3 py-1`} data-testid="billing-status">{meta.label}</Badge>
        </div>

        {sub.status === "trialing" && sub.trial_ends_at && (
          <p className="mt-4 text-sm text-slate-600" data-testid="trial-info">Your free trial ends on <strong>{new Date(sub.trial_ends_at).toLocaleDateString()}</strong>. Subscribe to keep access.</p>
        )}
        {sub.status === "past_due" && <p className="mt-4 text-sm text-red-600 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Payment failed. Please update your payment method.</p>}

        <ul className="mt-6 grid sm:grid-cols-2 gap-2">
          {FEATURES.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> {f}</li>)}
        </ul>

        {canManage && (
          <div className="mt-6 flex flex-wrap gap-3">
            {(sub.status === "trialing" || sub.status === "canceled" || sub.status === "none" || sub.status === "past_due") && (
              <Button onClick={startCheckout} disabled={busy} data-testid="subscribe-btn" className="bg-blue-600 hover:bg-blue-700">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-1" /> {sub.status === "canceled" ? "Reactivate" : "Subscribe now"}</>}
              </Button>
            )}
            {sub.status === "active" && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" className="text-red-600" data-testid="cancel-sub-btn">Cancel subscription</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Cancel subscription?</AlertDialogTitle><AlertDialogDescription>You'll keep access until the end of your billing period. You can reactivate anytime.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Keep plan</AlertDialogCancel><AlertDialogAction onClick={cancel} className="bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-sub">Cancel subscription</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {sub.status === "canceled" && <Button variant="outline" onClick={resume} data-testid="resume-sub-btn">Resume</Button>}
          </div>
        )}
        {!canManage && <p className="mt-6 text-sm text-slate-400">Only owners and admins can manage billing.</p>}
      </Card>

      <Card className="p-6 border-slate-200">
        <h3 className="font-heading font-semibold mb-1">Payment information</h3>
        <p className="text-sm text-slate-500">Card details are handled securely by Paddle — PlumbFlow never stores your payment card information.</p>
      </Card>
    </div>
  );
}
